import { BpIdUserAuditLogList } from "modules/system/sections/plt/bpid/components/BpIdUserAuditLogList";
import { BpIdAuditLogsFilter } from "modules/tenants/sections/plt/user/components/BpIdAuditLogsFilter";
import { DeleteUserDialog } from "modules/users/components/DeleteUserDialog";
import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { DATE_FORMATS, DateTime } from "@bps/date-time";
import {
  ActionButton,
  FontIcon,
  Heading,
  Separator,
  Stack,
  Text,
  TextBadge,
  TextBadgeColor,
  TextBadgeSize,
  useTheme
} from "@bps/fluent-ui";
import { ApiCallButton } from "@components/buttons/ApiCallButton";
import { dangerButtonStyles } from "@components/buttons/DangerButton";
import { CopyableGuid } from "@components/CopyableText";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import { withPermissions } from "@components/withPermissions";
import {
  BpIdUserDto,
  IdentityCategory
} from "@libs/api/gateways/bp-id/bp-id.dtos";
import {
  useBpIdUserAuditLogs,
  useBpIdUserDetails,
  useDeleteUserMutation,
  useLogoutAllSessions
} from "@libs/api/gateways/bp-id/bp-id.hooks";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { getPagesResult } from "@libs/paging/paging.utils";
import { Permissions } from "@libs/permissions/permissions.enum";

import { UsersTabs } from "../../users-tabs.constants";
import { CloudTenantsTable } from "./CloudTenantsTable";
import { FederatedIdentitiesTable } from "./FederatedIdentitiesTable";
import { MfaMethodsTable } from "./MfaMethodsTable";

const LOGIN_EVENT_TYPE = "Login";

type SignInIdentity = BpIdUserDto["signInIdentities"][number];

const isFederated = (i: SignInIdentity) =>
  i.category === IdentityCategory.premierFederated ||
  (i.signInType === "federated" && i.issuer === "bpcloud-remote-authn");

const formatDate = (iso?: string) =>
  iso ? DateTime.fromISO(iso).toFormat(DATE_FORMATS.DETAILED_DATE_TIME) : "-";

const BpIdAccountViewBase: React.FC = () => {
  const { objectId = "" } = useParams<{ objectId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  const userDetailsQuery = useBpIdUserDetails(objectId, {
    enabled: !!objectId
  });

  const { data: hasReadPermission } = usePermissionsCheckQuery(
    Permissions.PltBpidUserRead
  );

  const { data: hasClearPermission } = usePermissionsCheckQuery(
    Permissions.PltBpidUserClear
  );

  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltBpidUserWrite
  );

  const { data: hasAuditPermission } = usePermissionsCheckQuery(
    Permissions.PltBpidAuditRead
  );

  // Audit log is the source of truth; derive last successful sign-in from it.
  const auditLogsQuery = useBpIdUserAuditLogs(
    objectId,
    {},
    { enabled: !!objectId && !!hasAuditPermission }
  );

  const lastSignIn = useMemo(() => {
    const items = getPagesResult(auditLogsQuery.data);
    return items
      .filter(x => x.eventType === LOGIN_EVENT_TYPE && x.success && x.timeStamp)
      .sort((a, b) => (a.timeStamp < b.timeStamp ? 1 : -1))[0]?.timeStamp;
  }, [auditLogsQuery.data]);

  const {
    mutateAsync: deleteUser,
    isLoading: isDeleting,
    error: deleteError
  } = useDeleteUserMutation();

  const {
    mutateAsync: logoutAll,
    isLoading: isLoggingOut,
    error: logoutError
  } = useLogoutAllSessions();

  const [showDelete, setShowDelete] = useState(false);

  return (
    <Stack tokens={{ childrenGap: theme.spacing.s1, padding: theme.spacing.m }}>
      <ActionButton
        iconProps={{ iconName: "Back" }}
        onClick={() => navigate(-1)}
        styles={{
          root: {
            alignSelf: "start",
            padding: 0,
            height: "auto",
            marginBottom: theme.spacing.l1
          },
          label: {
            fontSize: theme.fonts.mediumPlus.fontSize,
            fontWeight: 600
          }
        }}
      >
        Back
      </ActionButton>
      <QueryStateIndicator {...userDetailsQuery}>
        {user => {
          const federated = user.signInIdentities?.filter(isFederated) ?? [];
          const distinctFederatedTenants = new Set(
            federated.map(i => i.tenantId).filter(Boolean)
          );

          // Only counts Premier (federated) tenants — CAM/Omni cloud tenants are
          // not affected by unlinking, so the warning is scoped to federated.
          const isLastFederatedTenant = distinctFederatedTenants.size <= 1;

          return (
            <Stack tokens={{ childrenGap: theme.spacing.m }}>
              {/* Header */}
              <Stack tokens={{ childrenGap: theme.spacing.m }}>
                <Stack tokens={{ childrenGap: 2 }}>
                  <Text bold>Object Id</Text>
                  <Stack
                    horizontal
                    verticalAlign="center"
                    tokens={{ childrenGap: theme.spacing.s1 }}
                  >
                    <CopyableGuid value={user.id} short />
                    <TextBadge
                      badgeSize={TextBadgeSize.small}
                      badgeColor={
                        user.accountEnabled
                          ? TextBadgeColor.green
                          : TextBadgeColor.red
                      }
                    >
                      <FontIcon
                        iconName={
                          user.accountEnabled ? "CompletedSolid" : "ErrorBadge"
                        }
                        styles={{ root: { marginRight: 4 } }}
                      />
                      {user.accountEnabled
                        ? "Cloud password"
                        : "No cloud password"}
                    </TextBadge>
                  </Stack>
                </Stack>

                <Stack tokens={{ childrenGap: 2 }}>
                  <Text bold>Name</Text>
                  <Text>
                    {user.displayName ||
                      `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim()}
                  </Text>
                </Stack>

                <Stack tokens={{ childrenGap: 2 }}>
                  <Text bold>Username</Text>
                  <Text>{user.signInEmail}</Text>
                </Stack>

                <Stack tokens={{ childrenGap: 2 }}>
                  <Text bold>Created</Text>
                  <Text>{formatDate(user.createdDate)}</Text>
                </Stack>

                <Stack tokens={{ childrenGap: 2 }}>
                  <Text bold>Last successful sign-in</Text>
                  <Text>{lastSignIn ? formatDate(lastSignIn) : "-"}</Text>
                </Stack>
              </Stack>

              {/* Account actions */}
              <Stack horizontal tokens={{ childrenGap: theme.spacing.s1 }}>
                {hasReadPermission && (
                  <ApiCallButton
                    isLoading={isLoggingOut}
                    error={logoutError}
                    onClick={() => logoutAll(user.id)}
                  >
                    Sign out all sessions
                  </ApiCallButton>
                )}
                {hasWritePermission && (
                  <ApiCallButton
                    isLoading={false}
                    error={undefined}
                    onClick={() => setShowDelete(true)}
                    styles={dangerButtonStyles(theme)}
                  >
                    Delete account
                  </ApiCallButton>
                )}
              </Stack>

              <DeleteUserDialog
                hidden={!showDelete}
                firstname={user.firstName}
                surname={user.lastName}
                isDeleting={isDeleting}
                error={deleteError?.message}
                onCancel={() => setShowDelete(false)}
                onDelete={async () => {
                  await deleteUser(user.id);
                  navigate(
                    `/users/${UsersTabs.BusinessUsers.IdentitySearch.id}`
                  );
                }}
              />

              <Separator styles={{ root: { maxWidth: 1200 } }} />

              {/* Identities */}
              <Stack tokens={{ childrenGap: theme.spacing.s2 }}>
                <Heading>Premier (federated) identities</Heading>
                {federated.length ? (
                  <FederatedIdentitiesTable
                    bpIdUserId={user.id}
                    identities={federated}
                    isLastFederatedTenant={isLastFederatedTenant}
                    canUnlink={!!hasClearPermission}
                  />
                ) : (
                  <Text>No Premier (federated) identities.</Text>
                )}

                <div style={{ height: theme.spacing.l1 }} aria-hidden />
                <Heading>Cloud tenants (CAM / Omni)</Heading>
                <CloudTenantsTable
                  bpIdUserId={user.id}
                  federatedTenantIds={federated
                    .map(f => f.tenantId)
                    .filter((t): t is string => !!t)}
                />
              </Stack>

              <Separator styles={{ root: { maxWidth: 1200 } }} />

              {/* MFA methods */}
              <Stack tokens={{ childrenGap: theme.spacing.s2 }}>
                <Heading>MFA methods</Heading>
                {user.mfaMethods?.length ? (
                  <MfaMethodsTable
                    bpIdUserId={user.id}
                    methods={user.mfaMethods}
                    refresh={() => userDetailsQuery.refetch()}
                  />
                ) : (
                  <Text>No MFA methods configured.</Text>
                )}
              </Stack>

              <Separator styles={{ root: { maxWidth: 1200 } }} />

              {/* Activity / audit log */}
              {hasAuditPermission && (
                <Stack tokens={{ childrenGap: theme.spacing.s2 }}>
                  <Heading>Activity log</Heading>
                  <BpIdAuditLogsFilter initialValues={{}}>
                    {({ values }) => (
                      <BpIdUserAuditLogList
                        bpIdUserId={user.id}
                        filters={values}
                      />
                    )}
                  </BpIdAuditLogsFilter>
                </Stack>
              )}
            </Stack>
          );
        }}
      </QueryStateIndicator>
    </Stack>
  );
};

export const BpIdAccountView = withPermissions(
  BpIdAccountViewBase,
  [
    Permissions.PltBpidUserRead,
    Permissions.PltBpidUserWrite,
    Permissions.PltBpidUserClear,
    Permissions.PltBpidMfaDelete
  ],
  "or"
);
